import { useCallback, useEffect, useRef, useState } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { CANVAS_INSTELLINGEN, Magazijn } from './magazijn/Magazijn'
import { Hoofdscherm } from './schermen/Hoofdscherm'
import { Module } from './schermen/Module'
import { useKiosk } from './state/store'
import { Instellingen } from './schermen/Instellingen'
import { Prestatie } from './kiosk/Prestatie'
import { useScene } from './scene/store'
import { PROFIEL_DETAIL } from './scene/config'
import { Videovenster } from './schermen/Videovenster'
import { Inactiviteit } from './schermen/Inactiviteit'
import { MODULES, t } from './content/content'
import { Product3DViewer } from './producten/Product3DViewer'
import { PRODUCTEN, vindProduct } from './producten/catalogus'
import { volgende } from './producten/laden'
import { Zweeffilm, ZweeffilmBrug } from './schermen/Zweeffilm'
import { useZweeffilm } from './kiosk/zweeffilm'

/**
 * Meldt zodra het magazijn daadwerkelijk getekend is.
 *
 * Het laadscherm hing eerder aan een `setTimeout` van 900 ms. Dat is een gok:
 * op de mini-pc kost het bouwen van de stellingen en het compileren van de
 * shaders meer dan dat, en dan gaat het gordijn omhoog boven een halfgebouwde
 * hal — precies wat het moest voorkomen. `useProgress` van drei helpt hier
 * niet: de scène is volledig procedureel en laadt geen enkel bestand, dus de
 * laadmanager staat meteen op klaar.
 *
 * Het tweede frame is wél een eerlijk signaal. Het eerste gaat op aan
 * shadercompilatie; staat het tweede er, dan is de hal echt getekend.
 */
function EersteFrame({ onKlaar }: { onKlaar: () => void }) {
  const frames = useRef(0)
  const gemeld = useRef(false)

  useFrame(({ gl, scene, camera }) => {
    if (gemeld.current) return
    // Alle shaders voorcompileren, ook van wat nu buiten beeld staat. Three
    // compileert een materiaal pas als het object voor het eerst in beeld
    // komt, en sinds de filmlook zijn dat er tientallen varianten (decals,
    // gaas, labels, folie). Zonder dit viel die rekening bij de eerste vlucht
    // naar elk scherm: een hapering midden in de beweging, en op ANGLE/D3D —
    // wat de Windows-kiosk draait — is een shadercompilatie geen kleinigheid.
    // Achter het laadscherm kost het niets wat een bezoeker ziet.
    if (frames.current === 0) gl.compile(scene, camera)
    if (++frames.current < 2) return
    gemeld.current = true
    onKlaar()
  })

  return null
}

export function App() {
  const scherm = useKiosk((s) => s.scherm)
  const taal = useKiosk((s) => s.taal)
  const zetTaal = useKiosk((s) => s.zetTaal)
  const [sceneGetekend, setSceneGetekend] = useState(false)
  const [noodgordijn, setNoodgordijn] = useState(false)
  const [minimumVoorbij, setMinimumVoorbij] = useState(false)
  const meldGetekend = useCallback(() => setSceneGetekend(true), [])

  // Ondergrens van 900 ms: is de scène er sneller, dan zou het laadscherm
  // flitsen. Het gordijn gaat dus weg zodra allebei waar is.
  //
  // De bovengrens van 8 s is de vangnetregel. Komt er geen WebGL-context — een
  // driver die weigert, een GPU-proces dat al bij het opstarten omvalt — dan
  // vuurt `useFrame` nooit en zou de kiosk de hele beurs op de laadbalk blijven
  // staan. Liever de UI boven een leeg canvas dan een scherm dat niets doet.
  //
  // Die twee redenen om het gordijn te lichten zijn bewust uit elkaar gehaald.
  // Ze zien er voor de bezoeker hetzelfde uit, maar ze betekenen het
  // tegenovergestelde: de één zegt "de hal staat er", de ander "we geven het
  // op". Zolang ze in één vlag zaten was dat verschil nergens af te lezen —
  // niet voor het afdrukscript, dat prompt afdrukken van een zwart canvas
  // maakte en er niets over meldde, en niet op de beursvloer, waar een kiosk
  // met een omgevallen GPU-proces er precies zo uitziet als een gezonde.
  useEffect(() => {
    const ondergrens = setTimeout(() => setMinimumVoorbij(true), 900)
    const bovengrens = setTimeout(() => setNoodgordijn(true), 8_000)
    return () => { clearTimeout(ondergrens); clearTimeout(bovengrens) }
  }, [])

  const geladen = (sceneGetekend || noodgordijn) && minimumVoorbij
  // De pixeldichtheid hoort bij het profiel (PLAN.md §0.10). Als prop op het
  // canvas en niet via setDpr: het canvas zet bij elke rerender zijn eigen
  // prop terug, en dan won [1, 2] het van een eerder gezette 1.
  const dpr = PROFIEL_DETAIL[useScene((s) => s.profiel)].dpr
  // Speelt op het hoofdscherm de film van de zweefronde, dan tekent de hal
  // niet: de film bedekt hem helemaal (Zweeffilm.tsx).
  const halStil = useZweeffilm((s) => s.halStil)

  // Het eerlijke signaal, op de wortel van het document: `getekend` betekent
  // dat de scène echt een tweede frame heeft gehaald. `wacht` betekent dat we
  // daar nog op staan — ook als het gordijn via het vangnet al omhoog is.
  useEffect(() => {
    document.documentElement.dataset.scene = sceneGetekend ? 'getekend' : 'wacht'
  }, [sceneGetekend])

  // De taalknop zet de content om; zonder dit blijft het document zelf op `nl`
  // staan, waar voorlezers en afbreking op afgaan.
  useEffect(() => {
    document.documentElement.lang = taal
  }, [taal])

  const module = MODULES.find((m) => m.id === scherm)
  const productId = useKiosk((s) => s.productViewer)
  const toonProduct = useKiosk((s) => s.toonProduct)
  const product = vindProduct(productId)

  return (
    <div className="kiosk">
      <div className="kiosk-canvas">
        <Canvas {...CANVAS_INSTELLINGEN} dpr={dpr} frameloop={halStil ? 'never' : 'always'}>
          {/* Binnen Magazijn, want daar staat de Suspense-grens: ernaast zou
              EersteFrame al melden terwijl de hal nog niet opgebouwd is. */}
          <Magazijn>
            <EersteFrame onKlaar={meldGetekend} />
          </Magazijn>
          <Prestatie />
          <ZweeffilmBrug />
        </Canvas>
        <Zweeffilm />
      </div>

      <div className="kiosk-scrim" />

      {/* Het hoofdscherm krijgt een eigen actiebalk (186 in plaats van 216
          eenheden) voor zes halve tegels van 260 × 138 — §2.5.2, §0a. */}
      <div className={`kiosk-ui${scherm === 'hoofdscherm' ? ' kiosk-hoofdscherm' : ''}`}>
        <header className="kiosk-kop">
          <div className="kiosk-merk">
            <Logo />
            <span>{module ? t(module.naam, taal) : 'Logistica Next 2026'}</span>
          </div>
          <button
            className="kiosk-taal"
            onClick={() => zetTaal(taal === 'nl' ? 'en' : 'nl')}
            aria-label={taal === 'nl' ? 'Switch to English' : 'Schakel naar Nederlands'}
          >
            {taal === 'nl' ? 'EN' : 'NL'}
          </button>
        </header>

        {scherm === 'hoofdscherm' ? <Hoofdscherm /> : <Module id={scherm} />}
      </div>

      <Instellingen />
      <Videovenster />
      <Inactiviteit />

      {/* De productviewer komt als losse laag boven de scène. Hij wordt pas
          gemonteerd als hij open is: een tweede WebGL-context die permanent
          meedraait naast de magazijnscène kost onnodig geheugen op de mini-pc. */}
      {product && (
        <Product3DViewer
          product={product}
          taal={taal}
          klasse="pv-overlay"
          onSluiten={() => toonProduct(null)}
          volgendProduct={volgende(PRODUCTEN, product.id)}
        />
      )}

      <div className={`kiosk-laden${geladen ? ' kiosk-weg' : ''}`}>
        <Logo groot />
        <div className="kiosk-laden-sub">From Warehouse to Powerhouse</div>
        <div className="kiosk-laden-balk"><i /></div>
      </div>
    </div>
  )
}

/**
 * Het combinatielogo uit het huisstijlhandboek (p. 4): het Linde-blok met
 * "motrac" ernaast — Logo 2, rechts. Als `<img>` en niet inline, zodat het
 * bestand ook door de service worker gecachet wordt zoals elk ander asset.
 */
function Logo({ groot = false }: { groot?: boolean }) {
  return (
    <img
      className={`kiosk-logo${groot ? ' kiosk-logo-groot' : ''}`}
      src="/motrac-logo.svg"
      alt="Linde Material Handling — Motrac"
      draggable={false}
    />
  )
}
